import { action, computed, get } from "@ember/object"
import { scheduleOnce } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import {
  followedCourseUnavailableReason,
  type FollowedCourseUnavailableReason,
} from "@shared/lib/followed-course"
import { CourseColor } from "@shared/models/colors"
import type {
  Course,
  CourseCalendarDateDefault,
  CourseCalendarTime,
  FollowedCourse,
} from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar, RotationCalendarRotation } from "@shared/models/rotation-calendar"
import type {
  ImportableCourse,
  ImportablePlanbook,
} from "cc-frontend/components/planner/modals/follow-course"
import { handleError } from "cc-frontend/error-handling"
import BelongTo from "cc-frontend/helpers/belong-to"
import FindDoc from "cc-frontend/helpers/find-doc"
import { hasFeature } from "cc-frontend/helpers/has-feature"
import { prepareCourseSetOneTimeAction } from "cc-frontend/lib/actions/course/calendar/COURSE_SET_ONE_TIME"
import changeCourseColorAction from "cc-frontend/lib/actions/course/COURSE_CHANGE_COLOR"
import changeCourseTitleAction from "cc-frontend/lib/actions/course/COURSE_CHANGE_TITLE"
import { prepareCourseFollowCourse } from "cc-frontend/lib/actions/course/COURSE_FOLLOW_COURSE"
import prepareCourseSetEnterTimesStateAction from "cc-frontend/lib/actions/course/COURSE_SET_ENTER_TIMES_STATE"
import setCourseNonInstructionalAction from "cc-frontend/lib/actions/course/COURSE_SET_NON_INSTRUCTIONAL"
import { prepareCourseUnfollowCourse } from "cc-frontend/lib/actions/course/COURSE_UNFOLLOW_COURSE"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import { addCourseTimeParts, parseCourseTimePart } from "cc-frontend/utils/course/times"
import {
  filterCourseDateDefault,
  isOnCourseDateDefault,
} from "cc-frontend/utils/filter-dates/course"
import { use } from "ember-could-get-used-to-this"
import { compact, debounce } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./name-and-more.module.css"

export interface NameAndMoreSig {
  Args: {
    course: Course
    findPreviousTime?: (course: Course) => CourseCalendarTime | null
    rotationCalendar: RotationCalendar
    planbook: Planbook
    showDeleteButton?: boolean
    hideDoneButton?: boolean
    close?: () => void
    /** Closes without the untitled-class guard `close` may carry, so deleting a
     * class the user never named can't strand them in its settings. */
    onDeleted?: () => void
  }
}

interface Day {
  rotation: RotationCalendarRotation
  courseDate: CourseCalendarDateDefault
  isOn: boolean
}

const courseColorsConst: Array<CourseColor> = [
  CourseColor.Yellow,
  CourseColor.MutedYellow,
  CourseColor.MutedGreen,
  CourseColor.Green,
  CourseColor.Turquoise,
  CourseColor.MutedTurquoise,
  CourseColor.MutedCyan,
  CourseColor.Cyan,
  CourseColor.Blue,
  CourseColor.MutedBlue,
  CourseColor.MutedPurple,
  CourseColor.Purple,
  CourseColor.Pink,
  CourseColor.MutedPink,
  CourseColor.MutedCoral,
  CourseColor.Coral,
]

export default class NameAndMoreComponent extends Component<NameAndMoreSig> {
  readonly styles = styles
  @service declare dispatcher: Dispatcher
  @service declare courseCalendarEditor: CourseCalendarEditor
  @service declare rpc: Rpc
  @service declare dialog: Dialog
  @service declare modalManager: ModalManager
  @service declare session: Session

  @tracked colorMenuIsOpen = false

  readonly courseColors = courseColorsConst

  // TITLE

  @action focusOnInput(element: HTMLInputElement) {
    scheduleOnce("afterRender", this, () => {
      if (this.isDestroyed || this.isDestroying) return

      if (!this.args.course.attributes.title) element?.focus()
    })
  }

  throttledSetTitle = (title: string) => {
    this._throttledSetTitle(title)
  }

  _throttledSetTitle = debounce(this.setTitle, 1000, { trailing: true })

  willDestroy(): void {
    this._throttledSetTitle.cancel()
  }

  @action
  setTitle(title: string) {
    this.dispatcher.dispatchPreparedAction(
      changeCourseTitleAction({
        courseId: this.args.course.id,
        title: title,
        originalTitle: this.args.course.attributes.title,
        planbookId: this.args.planbook.id,
      }),
    )
  }

  // COLOR

  @action
  setColor(color: CourseColor) {
    this.dispatcher.dispatchPreparedAction(
      changeCourseColorAction({
        courseId: this.args.course.id,
        color: color,
        planbookId: this.args.planbook.id,
      }),
    )
    this.colorMenuIsOpen = false
  }

  @action
  closeColorMenu() {
    this.colorMenuIsOpen = false
  }

  @action toggleColorMenu() {
    this.colorMenuIsOpen = !this.colorMenuIsOpen
  }

  // DAYS

  /** One entry per rotation day, in calendar order, with whether the class meets on it. */
  @computed(
    "args.course.attributes.calendar.version",
    "args.rotationCalendar.attributes.rotations.[]",
  )
  get days(): Array<Day> {
    const defaults = filterCourseDateDefault(this.args.course)
    const rotations = [...this.args.rotationCalendar.attributes.rotations].sort(
      (a, b) => a.position - b.position,
    )
    return compact(
      rotations.map((rotation) => {
        const courseDate = defaults.find((date) => date.attributes.rotationId === rotation.id)
        if (!courseDate) return null
        return { rotation, courseDate, isOn: isOnCourseDateDefault(this.args.course, courseDate) }
      }),
    )
  }

  @action
  toggleDay(day: Day) {
    const anotherActiveDayHasTime = this.days.some(
      (other) => other !== day && other.isOn && other.courseDate.attributes.time !== null,
    )
    this.courseCalendarEditor.toggleDefaultDate(
      this.args.course,
      this.args.rotationCalendar,
      day.rotation,
      day.courseDate.attributes.semesterId,
      day.isOn,
    )
    if (day.courseDate.attributes.time === null) return
    if (day.isOn && !anotherActiveDayHasTime) this.setTimeSetting("NO")
    if (!day.isOn) this.setTimeSetting("YES_DIFFERENT")
  }

  // TIMES

  @computed("args.course.attributes.calendar.version")
  get courseDateDefaults() {
    return filterCourseDateDefault(this.args.course)
  }

  /**
   * A day's time starts from a guess: a time another day of this class already has, else right
   * after the class before this one for the same length, else 8-9am.
   */
  @action
  addTime(day: Day) {
    const otherDaysTime =
      this.days.find(
        (other) => other !== day && other.isOn && other.courseDate.attributes.time !== null,
      )?.courseDate.attributes.time ?? null
    const previousTime = this.args.findPreviousTime?.(this.args.course) ?? null
    let time: CourseCalendarTime = { start: 8 * 60, end: 9 * 60 }
    if (otherDaysTime !== null) {
      time = otherDaysTime
    } else if (previousTime !== null) {
      time = {
        start: previousTime.end,
        end: addCourseTimeParts(previousTime.end, previousTime.end - previousTime.start),
      }
    }
    this.setOneTime(day, time)
    this.setTimeSetting("YES_DIFFERENT")
  }

  @action
  removeTime(day: Day) {
    this.setOneTime(day, null)
    const anotherDayHasTime = this.days.some(
      (other) => other !== day && other.isOn && other.courseDate.attributes.time !== null,
    )
    if (!anotherDayHasTime) this.setTimeSetting("NO")
  }

  setOneTime(day: Day, time: CourseCalendarTime | null) {
    this.dispatcher.dispatchPreparedAction(
      prepareCourseSetOneTimeAction({
        course: this.args.course,
        courseDate: day.courseDate,
        rotationTitle: day.rotation.title,
        planbookId: this.args.planbook.id,
        time,
      }),
    )
  }

  setTimeSetting(courseDateTimeSetting: Course["attributes"]["courseDateTimeSetting"]) {
    this.dispatcher.dispatchPreparedAction(
      prepareCourseSetEnterTimesStateAction({
        courseId: this.args.course.id,
        planbookId: this.args.planbook.id,
        courseDateTimeSetting,
      }),
    )
  }

  @action
  setOneStartTime(courseDate: CourseCalendarDateDefault, e: InputEvent) {
    const startTimeInputStr = (e.target as HTMLInputElement).value
    const start = parseCourseTimePart(startTimeInputStr)
    if (start === null) {
      console.log("Invalid time input", startTimeInputStr)
      return
    }
    let end = courseDate.attributes.time?.end ?? addCourseTimeParts(start, 60)
    if (end < start) end = start

    const rotationTitle = this.args.rotationCalendar.attributes.rotations.find(
      (rotation) => rotation.id === courseDate.attributes.rotationId,
    )?.title
    this.dispatcher.dispatchPreparedAction(
      prepareCourseSetOneTimeAction({
        course: this.args.course,
        courseDate,
        rotationTitle,
        planbookId: this.args.planbook.id,
        time: { start, end },
      }),
    )
  }

  @action
  setOneEndTime(courseDate: CourseCalendarDateDefault, e: InputEvent) {
    const endTimeInputStr = (e.target as HTMLInputElement).value
    let end = parseCourseTimePart(endTimeInputStr, courseDate.attributes.time?.start)
    if (end === null) {
      console.log("Invalid time input", endTimeInputStr)
      return
    }
    let start = courseDate.attributes.time?.start ?? addCourseTimeParts(end, -60)
    if (start > end) start = end

    const rotationTitle = this.args.rotationCalendar.attributes.rotations.find(
      (rotation) => rotation.id === courseDate.attributes.rotationId,
    )?.title
    this.dispatcher.dispatchPreparedAction(
      prepareCourseSetOneTimeAction({
        course: this.args.course,
        courseDate,
        rotationTitle,
        planbookId: this.args.planbook.id,
        time: { start, end },
      }),
    )
  }

  // LESSON PLAN OR JUST NOTES

  @action
  setNonInstructional(isNonInstructional: boolean) {
    if (isNonInstructional === this.args.course.attributes.isNonInstructional) return
    this.dispatcher.dispatchPreparedAction(
      setCourseNonInstructionalAction({
        course: this.args.course,
        planbookId: this.args.planbook.id,
        isNonInstructional,
      }),
    )
  }

  // FOLLOW A CLASS

  @use followedCourse = new FindDoc(() => [
    "course",
    get(this.args.course.attributes, "followedCourse")?.courseId ?? null,
  ])

  @use followedPlanbook = new BelongTo(() => [this.followedCourse.content, "planbook"])

  get showsFollowSection(): boolean {
    return (
      !!hasFeature([this.session.user?.content, "FOLLOW_CLASSES"]) &&
      !this.args.course.attributes.isNonInstructional &&
      !!this.args.planbook.attributes.flags.unifiedLessons &&
      this.args.course.meta.permissions.includes("can-edit-plan")
    )
  }

  /** Null until both documents land, or while the followed class can be shown. */
  get followedCourseUnavailableReason(): FollowedCourseUnavailableReason | null {
    const course = this.followedCourse.content
    const planbook = this.followedPlanbook.content
    if (!course || !planbook) return null
    return followedCourseUnavailableReason(course, planbook)
  }

  /** Null until both documents land, and whenever the followed class can't be opened from here. */
  get followedCourseProps(): {
    planbookId: string
    planbookTitle: string
    courseTitle: string
  } | null {
    const course = this.followedCourse.content
    const planbook = this.followedPlanbook.content
    if (!course || !planbook) return null
    if (followedCourseUnavailableReason(course, planbook) !== null) return null
    return {
      planbookId: planbook.id,
      planbookTitle: planbook.attributes.title,
      courseTitle: course.attributes.title,
    }
  }

  @action
  openFollowCourseModal() {
    this.modalManager.openModal("planner/modals/follow-course", {
      planbook: this.args.planbook,
      courseId: this.args.course.id,
      onSelectCourse: (_planbook: ImportablePlanbook, course: ImportableCourse) => {
        this.dispatchFollowCourse({ courseId: course.id }, course.title)
      },
    })
  }

  @action
  unfollowCourse() {
    this.dialog
      .blank("dialogs/confirm-unfollow-course", {
        className: "dialog--create-annotation",
      })
      .then(() => this.dispatchUnfollowCourse())
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }

  dispatchFollowCourse(followedCourseInfo: FollowedCourse, followedCourseTitle: string) {
    this.dispatcher.dispatchPreparedAction(
      prepareCourseFollowCourse({
        planbookId: this.args.planbook.id,
        courseId: this.args.course.id,
        courseTitle: this.args.course.attributes.title,
        followedCourseInfo,
        followedCourseTitle,
      }),
    )
  }

  dispatchUnfollowCourse() {
    this.dispatcher.dispatchPreparedAction(
      prepareCourseUnfollowCourse({
        planbookId: this.args.planbook.id,
        courseId: this.args.course.id,
        courseTitle: this.args.course.attributes.title,
        originalFollowedCourseInfo: this.args.course.attributes.followedCourse,
        originalFollowedCourseTitle: this.followedCourse.content?.attributes.title ?? null,
      }),
    )
  }

  // DELETE
  @action
  deleteClass(courseId: string) {
    // Captured up front: closing tears this component down, so `this.args` is
    // unreadable by the time the delete runs.
    const rpc = this.rpc
    const close = this.args.onDeleted ?? this.args.close
    this.dialog
      .blank("dialogs/confirm-delete-course", {
        className: "dialog--create-annotation",
      })
      .then(() => {
        close?.()
        rpc.execute("COURSE_DELETE", courseId)
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Course::CreateEdit::NameAndMore": typeof NameAndMoreComponent
  }
}
